import React, { useState, useCallback } from 'react';
import {
  Typography, Chip, Box, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AdminLayout from '../../components/AdminLayout';
import AdminTable from '../../components/AdminTable';
import apiClient from '../../services/apiClient';

// 999.18483 — POST /api/applications requires step-up (403 STEP_UP_REQUIRED).
function registerRefusalMessage(err) {
  if (err.data && err.data.error === 'STEP_UP_REQUIRED') {
    return 'Registering an application needs a recent two-factor check. Sign out and back in with your authenticator code, then try again.';
  }
  return err.message;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ApplicationsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ appId: '', name: '', base_url: '', api_url: '', callback_url: '' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchApps = useCallback(async (params) => {
    const data = await apiClient.get('/api/applications');
    // Client-side pagination for applications (typically small list)
    const apps = data.applications || [];
    const start = params.offset || 0;
    return {
      rows: apps.slice(start, start + (params.limit || 25)),
      total: apps.length,
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      await apiClient.post('/api/applications', formData);
      setShowForm(false);
      setFormData({ appId: '', name: '', base_url: '', api_url: '', callback_url: '' });
      setRefreshKey(k => k + 1);
    } catch (err) {
      setFormError(registerRefusalMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const columns = [
    {
      id: 'appId', label: 'App ID', minWidth: 120,
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.appId}
        </Typography>
      ),
    },
    { id: 'name', label: 'Name', minWidth: 140 },
    {
      id: 'base_url', label: 'Frontend URL', minWidth: 180,
      render: (row) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {row.base_url || '—'}
        </Typography>
      ),
    },
    {
      id: 'is_active', label: 'Status', minWidth: 80, sortable: false,
      render: (row) => (
        <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" variant="outlined"
          color={row.is_active ? 'success' : 'error'} sx={{ fontSize: '0.7rem' }} />
      ),
    },
    {
      id: 'auto_grant_new_users', label: 'Auto-Grant', minWidth: 90, sortable: false,
      render: (row) => row.auto_grant_new_users ? 'Yes' : 'No',
    },
    {
      id: 'created_at', label: 'Created', minWidth: 110,
      render: (row) => formatDate(row.created_at),
    },
  ];

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontFamily: "'EB Garamond', serif" }}>
          Applications
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(true)}
          sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
          Register App
        </Button>
      </Box>

      <AdminTable
        tableId="admin_applications"
        columns={columns}
        fetchData={fetchApps}
        refreshKey={refreshKey}
        defaultSort={{ field: 'created_at', dir: 'desc' }}
      />

      <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Register Application</DialogTitle>
          <DialogContent>
            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField size="small" label="App ID" required value={formData.appId}
                onChange={e => setFormData(d => ({ ...d, appId: e.target.value }))} />
              <TextField size="small" label="Name" required value={formData.name}
                onChange={e => setFormData(d => ({ ...d, name: e.target.value }))} />
              <TextField size="small" label="Frontend URL" value={formData.base_url}
                onChange={e => setFormData(d => ({ ...d, base_url: e.target.value }))} />
              <TextField size="small" label="API URL" value={formData.api_url}
                onChange={e => setFormData(d => ({ ...d, api_url: e.target.value }))} />
              <TextField size="small" label="Callback URL" value={formData.callback_url}
                onChange={e => setFormData(d => ({ ...d, callback_url: e.target.value }))} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </AdminLayout>
  );
}
